import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ClockCounterClockwise, User, FolderSimple, UsersThree, LinkSimple, UserPlus, Trash,
  ShieldCheck, MapPin, Browser, Globe, FunnelSimple, X, CaretDown, CaretRight,
} from '@phosphor-icons/react';
import { DateInput } from '../components/ui/DateInput';

// Icon + colour treatment for every audit action the Activity Log surfaces.
// Time-Travel share events are deliberately excluded (they live in the
// per-project Recipients dialog now) — anything not in this map falls back
// to a neutral clock icon.
const ACTION_ICONS = {
  invite_user: UserPlus,
  cancel_invitation: Trash,
  share_project: LinkSimple,
  unshare_project: LinkSimple,
  create_project: FolderSimple,
  delete_project: Trash,
  create_user: User,
  delete_user: Trash,
  login_2fa: ShieldCheck,
};

const ACTION_COLORS = {
  invite_user: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  cancel_invitation: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  share_project: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  unshare_project: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  create_project: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  delete_project: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  login_2fa: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
};

// Cheap UA → "Browser on OS" sniff so the row reads cleanly without bringing
// in a 30 KB UA-parser dependency for negligible gain.
function summariseUA(ua) {
  if (!ua) return '';
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
  else if (/python-requests/.test(ua)) browser = 'API (python)';
  else if (/curl\//.test(ua)) browser = 'curl';
  let os = '';
  if (/Windows NT 10/.test(ua)) os = 'Windows 10/11';
  else if (/Windows NT 6/.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  return os ? `${browser} on ${os}` : browser;
}

function geoText(row) {
  const parts = [row?.geo_city, row?.geo_region, row?.geo_country].filter(Boolean);
  return parts.join(', ');
}

export default function AuditLogPage() {
  const { apiCall, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 25;

  // ---- Filter state ----
  // All filters are URL-querystring-compatible strings (or "" for "All").
  // We hold them in a single object to make resetting clean and the change
  // handlers terse. Date strings stay as YYYY-MM-DD; the backend extends the
  // date_to value to end-of-day so the upper bound is inclusive.
  const [filters, setFilters] = useState({
    tenant_id: '',
    user_id: '',
    user_email: '',
    date_from: '',
    date_to: '',
    country: '',
    region: '',
    device: '',
  });
  const [opts, setOpts] = useState({ tenants: [], users: [], countries: [], regions: [], devices: [] });

  // Role-driven scope label — used in the header line.
  const isAdmin = user?.role === 'super_admin';
  const isTenantAdmin = user?.role === 'tenant_admin';

  useEffect(() => {
    // Load the dropdown population once on mount. The tenants list (for admin)
    // and the users list (for tenant_admin) is stable across the session, and
    // the country/region distinct values rarely move from minute to minute, so
    // a single fetch is enough — re-running only on user role change.
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCall('get', '/audit-logs/filter-options');
        if (!cancelled) setOpts(res.data || {});
      } catch (e) {
        if (!cancelled) console.warn('Failed to load filter options:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall, user?.role]);

  // Re-fetch logs whenever filters change (debounced lightly via the page
  // reset side-effect — changing any filter snaps us back to page 0).
  useEffect(() => { loadLogs(); }, [page, filters]);

  // Whenever a filter changes, reset to page 0 so the user isn't stranded on
  // an out-of-range page from the previous filter (e.g. was on page 5 of an
  // unfiltered list, then filters to country=India which only has 1 page).
  // Special case for country: if the new country no longer contains the
  // currently-selected region, also clear region so the user isn't sending a
  // stale combo to the backend (the backend would return 0 rows otherwise).
  const updateFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'country') {
        const stillValid = !prev.region || (opts.regions || []).some(
          (r) => r.code === prev.region && (!value || r.country === value)
        );
        if (!stillValid) next.region = '';
      }
      return next;
    });
    setPage(0);
  };
  const resetFilters = () => {
    setFilters({ tenant_id: '', user_id: '', user_email: '', date_from: '', date_to: '', country: '', region: '', device: '' });
    setPage(0);
  };

  const filtersActive = useMemo(
    () => Object.values(filters).some((v) => v && v !== ''),
    [filters],
  );

  // Cascade-filter the State / Region dropdown by selected country. If no
  // country is picked we surface the full list. We also de-duplicate by code
  // because the same region name can appear under multiple countries in a
  // global tenant (e.g. "Victoria" — Australia AND Canada).
  const visibleRegions = useMemo(() => {
    const rows = opts.regions || [];
    const filtered = filters.country ? rows.filter((r) => r.country === filters.country) : rows;
    const seen = new Set();
    const out = [];
    for (const r of filtered) {
      if (!r.code || seen.has(r.code)) continue;
      seen.add(r.code);
      out.push(r);
    }
    return out;
  }, [opts.regions, filters.country]);

  async function loadLogs() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), skip: String(page * limit) });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await apiCall('get', `/audit-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to load audit logs:', e);
    } finally { setLoading(false); }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    // 12-hour clock with am/pm so the time column reads more naturally for
    // US/AU users. `en-US` (instead of `en-GB`) gives us hour12 + lowercase
    // "am/pm". `hourCycle: 'h12'` is explicit in case the locale's default
    // ever changes upstream.
    return d.toLocaleDateString('en-GB') +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  function formatAction(action) {
    return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  const totalPages = Math.ceil(total / limit);

  // Track which row is expanded to show full details + raw user-agent. We
  // intentionally allow only one row open at a time — keeps the page short
  // and avoids stacked expandos that hide subsequent rows.
  const [expandedIdx, setExpandedIdx] = useState(null);

  // Quick lookups so we can render the tenant name next to each row for the
  // super-admin view (audit_logs store tenant_id, not the name).
  const tenantNameById = useMemo(() => {
    const map = {};
    (opts.tenants || []).forEach((t) => { map[t.id] = t.name; });
    return map;
  }, [opts.tenants]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="audit-log-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <ClockCounterClockwise size={22} className="text-primary" weight="duotone" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">Activity Log</h1>
            {/* Small filter glyph beside the title — replaces the inline
                "FILTERS" pill that was eating space in the filter bar. */}
            <FunnelSimple
              size={16}
              weight="bold"
              className="text-muted-foreground"
              title="Use the filters below to narrow this list"
              data-testid="audit-log-filter-icon"
            />
          </div>
          <p className="text-sm text-muted-foreground">{total} events recorded · 2FA logins, project sharing, user invites &amp; project changes with IP &amp; location</p>
        </div>
      </div>

      {/* Filter bar — surfaces the dropdowns the user picks; full-width on
          small screens, wrapping on desktop. Resetting clears every filter at
          once. Dropdown population (tenants/users/countries/regions) comes
          from /audit-logs/filter-options, scoped to the caller's role. */}
      <div className="mb-4 p-3 rounded-lg bg-card border border-border flex flex-wrap items-end gap-3" data-testid="audit-log-filters">
        {isAdmin && (
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Tenant</label>
            <select
              value={filters.tenant_id}
              onChange={(e) => updateFilter('tenant_id', e.target.value)}
              className="px-2 py-1.5 text-xs rounded-md bg-background border border-border text-foreground min-w-[150px]"
              data-testid="audit-filter-tenant"
            >
              <option value="">All tenants</option>
              {opts.tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {isTenantAdmin && (
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">User</label>
            <select
              value={filters.user_id}
              onChange={(e) => updateFilter('user_id', e.target.value)}
              className="px-2 py-1.5 text-xs rounded-md bg-background border border-border text-foreground min-w-[150px]"
              data-testid="audit-filter-user"
            >
              <option value="">All users</option>
              {opts.users.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Email</label>
          <input
            type="search"
            value={filters.user_email}
            onChange={(e) => updateFilter('user_email', e.target.value)}
            placeholder="Search by email…"
            className="px-2 py-1.5 text-xs rounded-md bg-background border border-border text-foreground min-w-[180px]"
            data-testid="audit-filter-email"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">From</label>
          <DateInput
            value={filters.date_from}
            onChange={(v) => updateFilter('date_from', v)}
            testId="audit-filter-from"
            placeholder="Pick a date"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">To</label>
          <DateInput
            value={filters.date_to}
            onChange={(v) => updateFilter('date_to', v)}
            testId="audit-filter-to"
            placeholder="Pick a date"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Country</label>
          <select
            value={filters.country}
            onChange={(e) => updateFilter('country', e.target.value)}
            className="px-2 py-1.5 text-xs rounded-md bg-background border border-border text-foreground min-w-[140px]"
            data-testid="audit-filter-country"
          >
            <option value="">All countries</option>
            {opts.countries.map((c) => (
              <option key={c.code} value={c.code}>{c.code} ({c.count})</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">State / Region</label>
          <select
            value={filters.region}
            onChange={(e) => updateFilter('region', e.target.value)}
            className="px-2 py-1.5 text-xs rounded-md bg-background border border-border text-foreground min-w-[140px]"
            data-testid="audit-filter-region"
          >
            <option value="">{filters.country ? `All states in ${filters.country}` : 'All states'}</option>
            {visibleRegions.map((r) => (
              <option key={`${r.country}-${r.code}`} value={r.code}>{r.code} ({r.count})</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Login source</label>
          <select
            value={filters.device}
            onChange={(e) => updateFilter('device', e.target.value)}
            className="px-2 py-1.5 text-xs rounded-md bg-background border border-border text-foreground min-w-[140px]"
            data-testid="audit-filter-device"
          >
            <option value="">Any device</option>
            {opts.devices?.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
        </div>

        {filtersActive && (
          <button
            type="button"
            onClick={resetFilters}
            className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-secondary hover:bg-secondary/70 text-foreground transition-colors"
            title="Clear all filters"
            data-testid="audit-filter-reset"
          >
            <X size={12} weight="bold" /> Reset
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <ClockCounterClockwise size={48} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No activity yet</p>
          <p className="text-sm mt-1">Actions will appear here as they happen.</p>
        </div>
      ) : (
        // Compact data table — one row per event, fixed columns, click any row
        // to expand for the full user-agent + raw details payload. Layout is
        // a CSS grid (not <table>) so we can use truncation, hover states and
        // expand-row animations without colspan gymnastics. Column widths are
        // chosen to keep the table readable down to ~1100px viewport — below
        // that the wrapper's overflow-x-auto lets it scroll horizontally.
        <div className="rounded-lg bg-card border border-border overflow-x-auto" data-testid="audit-log-table">
          {/* Header row */}
          <div
            className={`grid gap-x-3 px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-muted/40 border-b border-border ${
              isAdmin
                ? 'grid-cols-[16px_140px_150px_minmax(140px,1fr)_minmax(130px,1fr)_130px_minmax(190px,1.6fr)_minmax(120px,160px)]'
                : 'grid-cols-[16px_140px_minmax(160px,1fr)_minmax(130px,1fr)_130px_minmax(190px,1.6fr)_minmax(120px,160px)]'
            }`}
          >
            <span aria-hidden="true" />
            <span>Time</span>
            <span>{isAdmin ? 'Tenant' : 'User'}</span>
            {isAdmin && <span>User</span>}
            <span>Action</span>
            <span>IP address</span>
            <span>Location</span>
            <span>Device</span>
          </div>

          {/* Rows */}
          {logs.map((log, i) => {
            const Icon = ACTION_ICONS[log.action] || ClockCounterClockwise;
            const colorCls = ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
            const location = geoText(log);
            const device = summariseUA(log.user_agent);
            const ip = log.ip_address;
            const expanded = expandedIdx === i;
            const tenantName = log.tenant_id && tenantNameById[log.tenant_id] ? tenantNameById[log.tenant_id] : (log.tenant_id ? '—' : '—');
            const userLabel = log.user_name || log.user_email || '—';
            // Show email as a small secondary line under the name so
            // admins can spot "which employee did this" at a glance without
            // expanding the row. Suppressed when email is missing or is
            // literally the same string as the name (avoids double-noise).
            const showEmailSub = log.user_email && log.user_email !== log.user_name;
            return (
              <div key={log.id || i} className="border-b border-border last:border-b-0" data-testid={`audit-log-${i}`}>
                <button
                  type="button"
                  onClick={() => setExpandedIdx(expanded ? null : i)}
                  className={`w-full grid gap-x-3 items-center px-3 py-2 text-left text-xs hover:bg-muted/40 transition-colors ${
                    isAdmin
                      ? 'grid-cols-[16px_140px_150px_minmax(140px,1fr)_minmax(130px,1fr)_130px_minmax(190px,1.6fr)_minmax(120px,160px)]'
                      : 'grid-cols-[16px_140px_minmax(160px,1fr)_minmax(130px,1fr)_130px_minmax(190px,1.6fr)_minmax(120px,160px)]'
                  }`}
                  data-testid={`audit-log-${i}-row`}
                >
                  {/* Expand caret */}
                  <span className="text-muted-foreground shrink-0">
                    {expanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                  </span>

                  {/* Time — keep on one line, monospaced for column alignment. */}
                  <span className="text-foreground font-mono text-[11px] whitespace-nowrap font-medium">
                    {formatDate(log.created_at)}
                  </span>

                  {/* Admin: Tenant | Tenant_admin: User name first */}
                  {isAdmin ? (
                    <>
                      <span className="font-semibold text-foreground truncate" title={tenantName}>{tenantName}</span>
                      <span className="min-w-0 flex flex-col leading-tight" title={showEmailSub ? `${userLabel} · ${log.user_email}` : userLabel}>
                        <span className="text-foreground truncate">{userLabel}</span>
                        {showEmailSub && (
                          <span className="text-[11px] text-foreground/80 truncate" data-testid="audit-log-user-email">
                            {log.user_email}
                          </span>
                        )}
                      </span>
                    </>
                  ) : (
                    <span className="min-w-0 flex flex-col leading-tight" title={showEmailSub ? `${userLabel} · ${log.user_email}` : userLabel}>
                      <span className="font-semibold text-foreground truncate">{userLabel}</span>
                      {showEmailSub && (
                        <span className="text-[11px] text-foreground/80 truncate" data-testid="audit-log-user-email">
                          {log.user_email}
                        </span>
                      )}
                    </span>
                  )}

                  {/* Action chip — coloured to match the legacy card colour treatment. */}
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold w-fit ${colorCls}`}>
                    <Icon size={11} weight="bold" />
                    {formatAction(log.action)}
                  </span>

                  {/* IP — monospaced. Comes BEFORE Location per user request:
                      ops typically scan IPs first to match against alerts,
                      then read the geo for context. */}
                  <span className="text-foreground font-mono text-[11px] truncate font-medium" title={ip || ''}>
                    {ip || '—'}
                  </span>

                  {/* Location — full place string. Wider column to fit
                      "City, Region, Country" without truncation. */}
                  <span className="text-foreground truncate font-medium" title={location || ''}>
                    {location ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} weight="bold" className="shrink-0 opacity-70" />{location}
                      </span>
                    ) : '—'}
                  </span>

                  {/* Device — sniffed Browser-on-OS. */}
                  <span className="text-foreground truncate font-medium" title={log.user_agent || ''}>
                    {device || '—'}
                  </span>
                </button>

                {/* Expanded panel — shows the raw details + full UA when row is open. */}
                {expanded && (
                  <div className="px-9 py-3 bg-muted/20 border-t border-border" data-testid={`audit-log-${i}-expanded`}>
                    {log.details && (
                      <div className="mb-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Details</div>
                        <div className="text-xs text-foreground whitespace-pre-wrap break-words">{log.details}</div>
                      </div>
                    )}
                    {log.user_agent && (
                      <div className="mb-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Full user agent</div>
                        <div className="text-xs text-foreground font-mono break-words">{log.user_agent}</div>
                      </div>
                    )}
                    {log.user_email && log.user_email !== log.user_name && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Email</div>
                        <div className="text-xs text-foreground">{log.user_email}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1.5 text-xs font-medium rounded-md bg-card border border-border hover:bg-muted disabled:opacity-30">Previous</button>
          <span className="text-xs text-muted-foreground">Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-3 py-1.5 text-xs font-medium rounded-md bg-card border border-border hover:bg-muted disabled:opacity-30">Next</button>
        </div>
      )}
    </div>
  );
}
